import { RecommendHit } from "@algolia/recommend";
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useMemo } from "react";

import Grid from "~/components/common/grid";
import SkeletonProductTile from "~/components/common/skeletons/skeleton-product-tile";
import { useConfiguration } from "~/contexts/configuration";
import { useCartWithPendingStatus } from "~/hooks/use-cart";
import {
  cartRecommenderElegibleProduct,
  mergeHistWithProductsInformation,
  recommenderRequest,
} from "~/services/algolia-utils";
import Editorials from "~/types/editorials";
import appRoutes from "~/utils/app-routes";
import evaUtils from "~/utils/eva-utils";

import ProductCarouselSwiper from "./product-carousel-swiper";
import styles from "./product-carousel-swiper.module.scss";
type Props = {
  productReferenceFields: Editorials.ProductsAlgoliaRecommender;
  enableLoop: boolean;
  listScope: string;
};

export default function ProductCarouselAlgolia({ productReferenceFields, enableLoop, listScope }: Props) {
  const router = useRouter();
  const { cart } = useCartWithPendingStatus();
  const { eva, algoliaIndexId } = useConfiguration();

  const isCartPage = useMemo(() => {
    return router.pathname === appRoutes.CART;
  }, [router]);

  const productID = useMemo(() => {
    if (!isCartPage) {
      return (router.query.slug as string)?.split("-").at(-1) ?? "";
    } else {
      if (productReferenceFields?.type === "trending-items") {
        return "";
      } else {
        const latestCarProduct = cartRecommenderElegibleProduct(cart);
        return latestCarProduct ? latestCarProduct : "";
      }
    }
  }, [cart, isCartPage, productReferenceFields, router]);

  const carouselDataQuery = useMemo(() => {
    return [
      "algolia-product-carousel",
      productReferenceFields?.type ?? "strategy-not-defined",
      !!productID ? productID : "trending-items",
    ];
  }, [productID, productReferenceFields]);

  const { data, isLoading } = useQuery({
    queryKey: carouselDataQuery,
    staleTime: Infinity,
    queryFn: async () => {
      if (productReferenceFields && productReferenceFields.type === "bought-together" && !productID) {
        return [];
      }

      const response = await recommenderRequest({
        algoliaIndexId,
        productReferenceFields,
        productID: productID || undefined,
      });
      if (response) {
        const productIDS = (response.hits as RecommendHit[]).map((item) => item.objectID);
        //Removed hook
        const productsInformation = await evaUtils.getProductsByIdsWithChildren(eva, productIDS);
        const mergedData = mergeHistWithProductsInformation(
          response.hits as RecommendHit[],
          productsInformation,
          response.queryID
        );
        return mergedData;
      } else {
        return [];
      }
    },
  });
  return (
    <>
      {isLoading ? (
        // Here should be placed a loader
        <Grid className={styles.preloadGrid}>
          {Array.from({ length: 5 }).map((_, i) => (
            <SkeletonProductTile key={`preload-${i}`} />
          ))}
        </Grid>
      ) : (
        <ProductCarouselSwiper isAlgolia products={data} listScope={listScope} enableLoop={enableLoop} />
      )}
    </>
  );
}
